import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { goToGivenSlotForm, goToPatientForm } from 'FRONTEND/reducers/calendarSlice';
import { userUpdate } from 'FRONTEND/reducers/userSlice';
import { getSettings, postSettings } from 'FRONTEND/utils/backendApi/settingsApi';
import { DEFAULT_SETTINGS, MAX_CHECKED_EXPERT_COUNT } from 'FRONTEND/utils/constants';
import { getDeserializedState } from 'FRONTEND/utils/serialization';

dayjs.extend(isBetween);

const REDUCER_NAME = 'expert';

const expertAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.alias?.localeCompare(b.alias),
});

export const listSettings = createAsyncThunk(`${REDUCER_NAME}/listSettings`, async() => {
  return {
    settingsList: await getSettings(),
  };
});

export const editSettings = createAsyncThunk(`${REDUCER_NAME}/editSettings`, ({ expertId, settings, showSnackbar }) => {
  return postSettings({
    expertId,
    settings,
    showSnackbar,
  });
});

const storedState = getDeserializedState()?.[REDUCER_NAME];
const initialState = {
  ...expertAdapter.getInitialState(),
  matchingIdList: [],
  error: null,
  checkedList: [],
  managedIdList: [],
  filterText: '',
  ...storedState,
};

const expertSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    searchRequest() {},
    searchSuccess(state, { payload }) {
      state.matchingIdList = payload.experts.map((expert) => expert.id);
      expertAdapter.setMany(state, payload.experts);
    },
    searchError(state, { payload }) {
      state.error = payload.error;
    },
    clearSearch(state) {
      state.matchingIdList = [];
    },
    check(state, { payload: { id } }) {
      if (!state.checkedList.includes(id) && state.checkedList.length < MAX_CHECKED_EXPERT_COUNT) {
        state.checkedList.push(id);
      }
    },
    unCheck(state, { payload: { id } }) {
      state.checkedList = state.checkedList.filter((checkedId) => checkedId !== id);
    },
    checkFive(state, { payload: { itemList } }) {
      itemList.forEach(item => {
        if (!state.checkedList.includes(item.id) && state.checkedList.length < 5) {
          state.checkedList.push(item.id);
        }
      });
    },
    changeExpertFilterText(state, { payload: filterText }) {
      state.filterText = filterText;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userUpdate, (state, { payload: { managedExpertsList } }) => {
      if (managedExpertsList?.length > 0) {
        const fullExperts = managedExpertsList.map((expert) => ({
          ...DEFAULT_SETTINGS,
          ...expert,
        }));
        expertAdapter.setAll(state, fullExperts);
        const managedIdList = managedExpertsList.map(({ id }) => id);
        state.managedIdList = managedIdList;
        state.checkedList = state.checkedList.filter((id) => managedIdList.includes(id));
      }
    });
    builder.addCase(listSettings.fulfilled, (state, { payload: { settingsList } }) => {
      expertAdapter.upsertMany(state, settingsList);
    });
    builder.addCase(editSettings.fulfilled, (state, { payload: { id, ...settings } }) => {
      expertAdapter.updateOne(state, {
        id,
        changes: settings,
      });
    });
    builder.addMatcher(
      (action) => [goToGivenSlotForm.toString(), goToPatientForm.toString()].includes(action.type),
      (state) => {
        state.matchingIdList = [];
      },
    );
    builder.addDefaultCase(() => {});
  },
});


export const {
  selectById: selectManagedExpertById,
  selectAll: selectAllExperts,
  selectEntities: selectExpertEntities,
} = expertAdapter.getSelectors((globalState) => globalState.expert);

export const {
  searchRequest,
  searchSuccess,
  searchError,
  clearSearch,
  check,
  unCheck,
  checkFive,
  changeExpertFilterText,
} = expertSlice.actions;
export default expertSlice.reducer;
